import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Play, Pause, Square, SkipBack, SkipForward, Volume2, VolumeX, Settings, RefreshCw, Loader2 } from 'lucide-react';
import { GlobalAudioService } from '@/services/audio/GlobalAudioService';
import { audioMetadataCache } from '@/services/audio/AudioMetadataCache';
import { useToast } from '@/hooks/use-toast';
import { SpeedPresetButtons } from './SpeedPresetButtons';
import { SectionNavigationButtons } from './SectionNavigationButtons';
import { Badge } from '@/components/ui/badge';
import { AudioDebugBadge } from './AudioDebugBadge';
import { performanceMonitor } from '@/services/PerformanceMonitorService';

interface WorldClassAudioPlayerProps {
  text: string;
  moduleId: string;
  sectionId: string;
  courseId: string;
  title?: string;
  onComplete?: () => void;
}

export const WorldClassAudioPlayer: React.FC<WorldClassAudioPlayerProps> = ({
  text,
  moduleId,
  sectionId,
  courseId,
  title = 'Audio Content',
  onComplete,
}) => {
  const [audioState, setAudioState] = useState(GlobalAudioService.getState());
  const [rate, setRate] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initStartTime] = useState(Date.now());
  const { toast } = useToast();
  const mountedRef = React.useRef(true);

  // Track loading time for progressive states
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingTime(Date.now() - initStartTime);
    }, 500);
    
    return () => clearInterval(interval);
  }, [initStartTime]);

  useEffect(() => {
    console.log('🎧 WorldClassAudioPlayer mounted:', { 
      textLength: text.length, 
      moduleId, 
      sectionId 
    });

    if (!text || text.trim().length === 0) {
      console.error('❌ No text provided to audio player');
      setIsInitializing(false);
      return;
    }

    if (!moduleId) {
      console.warn('⚠️ No moduleId provided, using "default"');
    }
    
    // PHASE 3: Bulletproof audio initialization with comprehensive error handling
    // PHASE 1 FIX: Defer audio init by 500ms to prioritize content rendering
    const initAudio = async () => {
      const operationId = `audio_${moduleId}_${Date.now()}`;
      performanceMonitor.startTimer(operationId);
      
      try {
        // Check if still mounted before initializing
        if (!mountedRef.current) {
          console.log('🚫 Component unmounting - skipping audio init');
          return;
        }
        
        // PHASE 3: Wrap in additional try-catch for GlobalAudioService call
        try {
          await GlobalAudioService.initializeFromDbAudio({
            text,
            moduleId,
            courseId,
            moduleTitle: title,
            courseTitle: title,
          });
        } catch (audioServiceError) {
          console.warn('[WorldClassAudioPlayer] GlobalAudioService init failed:', audioServiceError);
          throw audioServiceError; // Re-throw to be caught by outer catch
        }
        
        // Record successful audio load time
        const loadTime = performanceMonitor.endTimer(operationId, 'audio_load', { 
          moduleId, 
          courseId,
          success: true 
        });
        
        console.log(`🎵 Audio initialized in ${Math.round(loadTime)}ms`);
        
        // Check again after async operation
        if (mountedRef.current) {
          setIsInitializing(false);
        }
      } catch (error) {
        // PHASE 3: Never throw errors - always gracefully degrade
        console.warn('[WorldClassAudioPlayer] Audio init failed (non-critical):', error);
        
        // Record failed audio load
        performanceMonitor.endTimer(operationId, 'audio_load', { 
          moduleId, 
          courseId,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        
        if (mountedRef.current) {
          setIsInitializing(false);
        }
        // PHASE 3: Explicitly do NOT re-throw - let page render without audio
      }
    };

    // PHASE 1 FIX: Defer audio initialization by 500ms
    const timer = setTimeout(() => {
      initAudio();
    }, 500);
    
    const unsubscribe = GlobalAudioService.subscribe((state) => {
      if (!mountedRef.current) return;
      console.log('🔄 Audio state changed:', state);
      setAudioState(state);
      setRate(state.rate);
      setVolume(state.volume);
      
      if (state.progress >= 100 && onComplete) {
        onComplete();
      }
    });

    return () => {
      clearTimeout(timer);
      console.log('🎧 WorldClassAudioPlayer unmounting');
      mountedRef.current = false;
      unsubscribe();
    };
  }, [text, moduleId, sectionId, courseId]);

  // PHASE 3: Bulletproof retry with comprehensive error handling
  const handleRetry = useCallback(() => {
    setIsInitializing(true);
    setLoadingTime(0);
    
    // PHASE 3: Wrap retry in try-catch
    try {
      GlobalAudioService.initializeFromDbAudio({
        text,
        moduleId,
        courseId,
        moduleTitle: title,
        courseTitle: title,
      }).then(() => {
        setIsInitializing(false);
        toast({
          title: "Audio reloaded",
          description: "Audio player has been reloaded successfully."
        });
      }).catch((error) => {
        console.warn('[WorldClassAudioPlayer] Retry failed:', error);
        setIsInitializing(false);
        toast({
          title: "Retry failed",
          description: "Unable to load audio. Please try again later.",
          variant: "destructive"
        });
      });
    } catch (error) {
      console.warn('[WorldClassAudioPlayer] Retry initialization failed:', error);
      setIsInitializing(false);
    }
  }, [text, moduleId, courseId, title, toast]);

  const handlePlayPause = useCallback(() => {
    console.log('🎮 Play/Pause button clicked');
    
    if (audioState.isPlaying && !audioState.isPaused) {
      GlobalAudioService.pause();
    } else {
      GlobalAudioService.play();
    }
  }, [audioState.isPlaying, audioState.isPaused]);

  const handleStop = useCallback(() => {
    console.log('⏹️ Stop button clicked');
    GlobalAudioService.stop();
  }, []);

  const handleRateChange = useCallback((value: number[] | number) => {
    const newRate = Array.isArray(value) ? value[0] : value;
    setRate(newRate);
    GlobalAudioService.setRate(newRate);
  }, []);

  const handleVolumeChange = useCallback((value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    GlobalAudioService.setVolume(newVolume);
  }, []);

  const handleMuteToggle = useCallback(() => {
    const newVolume = isMuted ? 0.8 : 0;
    setVolume(newVolume);
    setIsMuted(!isMuted);
    GlobalAudioService.setVolume(newVolume);
  }, [isMuted]);

  const handleSkipBack = useCallback(() => {
    GlobalAudioService.skipBackward(30);
  }, []);

  const handleSkipForward = useCallback(() => {
    GlobalAudioService.skipForward(30);
  }, []);

  const handleProgressSeek = useCallback((value: number[]) => {
    const newProgress = value[0];
    const engine = GlobalAudioService.getEngine();
    if (!engine || !audioState) return;
    
    const newTime = (newProgress / 100) * audioState.duration;
    const wasPlaying = audioState.isPlaying;
    
    // Seek to new position
    GlobalAudioService.seekTo(newTime);
    
    // Resume playing if it was playing before
    if (wasPlaying) {
      setTimeout(() => GlobalAudioService.play(), 150);
    }
  }, [audioState]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = useCallback((time: number) => {
    GlobalAudioService.seekTo(time);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      switch (e.key.toLowerCase()) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'arrowleft':
          e.preventDefault();
          handleSkipBack();
          break;
        case 'arrowright':
          e.preventDefault();
          handleSkipForward();
          break;
        case 's':
          e.preventDefault();
          handleStop();
          break;
        case 'm':
          e.preventDefault();
          handleMuteToggle();
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
          e.preventDefault();
          const speeds = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
          const speed = speeds[parseInt(e.key) - 1];
          if (speed) handleRateChange(speed);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePlayPause, handleSkipBack, handleSkipForward, handleStop, handleMuteToggle, handleRateChange]);

  // PHASE 3 & 4: Always show player with progressive loading/error states
  const renderPlayerStatus = () => {
    // Error state - show inline with retry
    if (audioState.error === 'No audio available' || audioState.error === 'Failed to load audio') {
      return (
        <div className="space-y-2 py-2">
          <div className="flex items-center gap-2 text-sm text-orange-600">
            <Volume2 className="h-4 w-4" />
            <span>
              {audioState.error === 'No audio available' 
                ? '🎧 Audio for this lesson is being generated'
                : '⚠️ Unable to load audio'}
            </span>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRetry} size="sm" variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              {audioState.error === 'No audio available' ? 'Check Again' : 'Retry'}
            </Button>
            <Button 
              onClick={() => {
                audioMetadataCache.forceRefresh(moduleId, courseId);
                handleRetry();
              }} 
              size="sm" 
              variant="ghost" 
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Clear Cache & Retry
            </Button>
          </div>
        </div>
      );
    }

    // Loading state
    if (isInitializing) {
      if (loadingTime < 5000) {
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Initializing audio player...</span>
          </div>
        );
      } else if (loadingTime < 15000) {
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading audio... (checking database)</span>
          </div>
        );
      } else {
        return (
          <div className="space-y-2 py-2">
            <div className="flex items-center gap-2 text-sm text-orange-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>This is taking longer than expected...</span>
            </div>
            <Button onClick={handleRetry} size="sm" variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Force Reload
            </Button>
          </div>
        );
      }
    }

    return null;
  };

  // PHASE 3: REMOVED - No longer hide player completely
  // Player is ALWAYS visible with appropriate state indicators

  return (
    <>
      {/* Audio Debug Badge - Dev mode only */}
      <AudioDebugBadge
        moduleId={moduleId}
        courseId={courseId}
        audioUrl={audioState.audioUrl || null}
        isLoading={isInitializing}
        error={audioState.error}
        loadTime={loadingTime}
      />
      
      {/* PHASE 1: Always-visible Audio Player Card */}
      <Card className="relative overflow-hidden border border-border/50 bg-background">
        <div className="relative space-y-3 p-3">
          {/* Header - Title, Source Badge & Progress */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1">
              <h3 className="text-sm font-medium text-foreground truncate">{title}</h3>
              {!isInitializing && (
                <Badge variant="outline" className="gap-1 border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400 text-xs">
                  <Volume2 className="h-2.5 w-2.5" />
                  Professional Audio
                </Badge>
              )}
            </div>
            {audioState.progress > 0 && (
              <div className="text-xs font-medium text-primary">
                {Math.round(audioState.progress)}%
              </div>
            )}
          </div>

          {/* PHASE 3: Always show status (loading or error) */}
          {renderPlayerStatus()}

          {/* Main Controls - Always visible */}
          <div className="flex items-center gap-2">
            {/* Skip Back */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={handleSkipBack}
              disabled={audioState.isStopped || isInitializing || !!audioState.error}
              aria-label="Skip back 10 seconds"
            >
              <SkipBack className="h-4 w-4" />
            </Button>

            {/* Play/Pause Button - disabled during initialization or errors */}
            <Button
              size="icon"
              className="h-10 w-10 rounded-full shadow-md hover:shadow-lg transition-all"
              onClick={handlePlayPause}
              disabled={isInitializing || !!audioState.error}
              aria-label={audioState.isPlaying && !audioState.isPaused ? 'Pause' : 'Play'}
            >
              {audioState.isPlaying && !audioState.isPaused ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </Button>

            {/* Skip Forward */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={handleSkipForward}
              disabled={audioState.isStopped || isInitializing || !!audioState.error}
              aria-label="Skip forward 10 seconds"
            >
              <SkipForward className="h-4 w-4" />
            </Button>

            {/* Stop */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={handleStop}
              disabled={audioState.isStopped || isInitializing || !!audioState.error}
              aria-label="Stop"
            >
              <Square className="h-4 w-4" />
            </Button>

            {/* Volume Control - Inline */}
            <div className="flex items-center gap-1 ml-auto">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleMuteToggle}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
              <div className="w-20 hidden sm:block">
                <Slider
                  value={[isMuted ? 0 : volume]}
                  onValueChange={handleVolumeChange}
                  min={0}
                  max={1}
                  step={0.05}
                  className="cursor-pointer"
                  aria-label="Volume"
                />
              </div>
            </div>

            {/* Speed Display */}
            <div className="text-xs font-medium text-muted-foreground min-w-[32px] text-center hidden sm:block">
              {rate.toFixed(1)}x
            </div>

            {/* Advanced Settings Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => setShowAdvanced(!showAdvanced)}
              aria-label="Advanced settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress Bar - Draggable */}
          {(audioState.isPlaying || audioState.progress > 0) && (
            <div className="space-y-1">
              <Slider
                value={[audioState.progress]}
                onValueChange={handleProgressSeek}
                min={0}
                max={100}
                step={0.1}
                className="cursor-pointer"
                aria-label="Audio progress"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatTime(audioState.currentTime)}</span>
                <span>{formatTime(audioState.duration)}</span>
              </div>
            </div>
          )}

          {/* Collapsible Advanced Controls */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleContent className="space-y-3 pt-3 border-t border-border/50">
              {/* Speed Control */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground">Speed</label>
                  <span className="text-xs text-muted-foreground">{rate.toFixed(1)}x</span>
                </div>
                <SpeedPresetButtons currentRate={rate} onRateChange={handleRateChange} />
                <Slider
                  value={[rate]}
                  onValueChange={handleRateChange}
                  min={0.5}
                  max={2}
                  step={0.1}
                  className="cursor-pointer"
                  aria-label="Playback speed"
                />
              </div>

              {/* Volume Control - Mobile */}
              <div className="space-y-2 sm:hidden">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground">Volume</label>
                  <span className="text-xs text-muted-foreground">
                    {Math.round((isMuted ? 0 : volume) * 100)}%
                  </span>
                </div>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  onValueChange={handleVolumeChange}
                  min={0}
                  max={1}
                  step={0.05}
                  className="cursor-pointer"
                  aria-label="Volume"
                />
              </div>

              {/* Section Navigation */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Sections</label>
                <SectionNavigationButtons
                  content={text}
                  currentWordIndex={audioState.currentWordIndex}
                  duration={audioState.duration}
                  onSeek={handleSeek}
                  isPlaying={audioState.isPlaying}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </Card>
    </>
  );
};

export default WorldClassAudioPlayer;
